import { ClockCounterClockwise } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useActivity } from "~/api/queries";
import { Row, Rows } from "~/features/automation/components";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

const PAGE_SIZE = 25;

/**
 * A chronological record of what changed and who changed it.
 *
 * Event names (`documents.publish`) are rendered verbatim rather than translated: they are
 * identifiers the server defines, they appear in the audit export and in support questions,
 * and a prettified name that does not match what the API says is worse than a technical one
 * that does.
 */
export function Activity() {
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isFetching } = useActivity({ offset, limit: PAGE_SIZE });

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!data?.events.length) {
    return <Empty icon={ClockCounterClockwise} message={t("Nothing has happened yet.")} />;
  }

  const shown = offset + data.events.length;

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t("Showing {{shown}} of {{total}}", { shown, total: data.total })}
      </Text>

      <Rows>
        {data.events.map((event) => (
          <Row key={event.id}>
            <Avatar
              name={event.actor?.name ?? "?"}
              src={event.actor?.avatarUrl ?? null}
              size={24}
            />
            <span className="min-w-0 flex-1 truncate text-body text-text">
              {event.actor?.name ?? t("Someone")}
            </span>
            <span className="truncate font-mono text-small text-text-secondary">{event.name}</span>
            <span className="hidden w-24 shrink-0 text-small text-text-tertiary sm:inline">
              {formatRelativeTime(event.createdAt, i18n.language)}
            </span>
          </Row>
        ))}
      </Rows>

      <div className="flex items-center justify-between gap-2">
        <Button
          size="sm"
          disabled={offset === 0 || isFetching}
          onClick={() => setOffset((previous) => Math.max(0, previous - PAGE_SIZE))}
        >
          {t("Newer")}
        </Button>
        <Button
          size="sm"
          disabled={shown >= data.total || isFetching}
          onClick={() => setOffset((previous) => previous + PAGE_SIZE)}
        >
          {t("Older")}
        </Button>
      </div>
    </div>
  );
}
